// adminportal/src/components/Logo.tsx
import React from 'react';
import { Shield } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';

interface LogoProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'admin';
  className?: string;
  showText?: boolean;
}

const sizeClasses = {
  xs: 'w-1 h-1', // 1x1px for login pages
  sm: 'w-6 h-6',
  md: 'w-8 h-8',
  lg: 'w-12 h-12',
  xl: 'w-16 h-16',
  '2xl': 'w-20 h-20',
  '3xl': 'w-24 h-24',
  '4xl': 'w-32 h-32',
  admin: 'w-64 h-32', // 4 inches wide (256px) by 2 inches tall (128px) - large but not overwhelming
};

export const Logo: React.FC<LogoProps> = ({ size = 'md', className = '', showText = false }) => {
  const { settings } = useSettings();
  const sizeClass = sizeClasses[size];

  // Debug logging (commented out to prevent spam)
  // console.log('🔍 Logo component debug:', {
  //   settings: settings,
  //   companyLogo: settings.company.logo,
  //   companyName: settings.company.name,
  //   hasLogo: !!settings.company.logo,
  // });

  if (settings.company.logo) {
    // For uploaded logos, use appropriate size even for xs
    const logoSizeClass = size === 'xs' ? 'w-32 h-32' : sizeClass; // Use larger size for xs when logo exists
    return (
      <div className={`flex items-center justify-center w-full ${className}`}>
        <img
          src={settings.company.logo}
          alt={`${settings.company.name} Logo`}
          className={`${logoSizeClass} object-contain object-center max-w-full h-auto`}
          onError={e => {
            console.error('❌ Logo image failed to load:', settings.company.logo);
            console.error('❌ Image error:', e);
          }}
          onLoad={() => {
            console.log('✅ Logo image loaded successfully:', settings.company.logo);
          }}
        />
        {showText && <span className='ml-2 font-bold text-lg'>{settings.company.name}</span>}
      </div>
    );
  }

  // Fallback - for login pages use 1x1px transparent pixel, otherwise show shield
  if (size === 'xs') {
    return (
      <div className={`flex items-center justify-center w-full ${className}`}>
        <div className={`${sizeClass} bg-transparent`} />
        {showText && <span className='ml-2 font-bold text-lg'>{settings.company.name}</span>}
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-center w-full ${className}`}>
      <div
        className={`${sizeClass} bg-gradient-to-br from-purple-500 to-cyan-500 rounded-lg flex items-center justify-center`}
      >
        <Shield className='w-1/2 h-1/2 text-white' />
      </div>
      {showText && <span className='ml-2 font-bold text-lg'>{settings.company.name}</span>}
    </div>
  );
};

export default Logo;
